import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { scheduleDays, scheduleFromDays } from '@goaltree/backend/convex/lib/schedule'
import { Calendar } from '@goaltree/ui/components/calendar'
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@goaltree/ui/components/popover'
import { Sheet, SheetContent } from '@goaltree/ui/components/sheet'
import { enUS, es, fr, ja, pt, zhCN } from '@goaltree/ui/lib/locales'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import i18next from 'i18next'
import { Clock, Plus, RefreshCw, Sun, Trash2, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AskCoachComposer,
  AskCoachThread,
  useAskChips,
  useCoachPersona,
} from '@/components/ask-coach'
import {
  DetailActions,
  DetailKindLabel,
  DetailMenu,
  type DetailMenuItem,
  DetailPartOf,
  type DetailPathNode,
  DetailPrimaryAction,
  DetailRow,
  DetailRows,
} from '@/components/detail-panel'
import { HabitTimePicker } from '@/components/habit-time-picker'
import { MilestoneProgress } from '@/components/plan-outline'
import { RecursDaysChips } from '@/components/recurs-days-chips'
import { SavedHint, useSavedHint } from '@/components/saved-hint'
import { TaskDurationPicker } from '@/components/task-duration-picker'
import { TaskNoteField } from '@/components/task-note-field'
import { TaskStatusIcon } from '@/components/task-status-icon'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'
import { useOnlineStatus } from '@/hooks/use-online-status'
import { useSetTaskStatus } from '@/hooks/use-set-task-status'
import { useStickToBottom } from '@/hooks/use-stick-to-bottom'
import { useTaskExplain } from '@/hooks/use-task-explain'
import { caretOffsetFromPoint } from '@/lib/caret'
import { CATEGORY_COLORS } from '@/lib/category-color'
import {
  completeNode,
  optimisticAllDay,
  optimisticDueDate,
  optimisticDuration,
  optimisticRecurs,
  optimisticRename,
  uncompleteNode,
} from '@/lib/nodes-optimistic'
import { periodLabel } from '@/lib/schedule'
import { playDoneSound } from '@/lib/sounds'
import { parseTaskTitle } from '@/lib/tags'
import {
  coerceEnd,
  isDueTodayOrOverdue,
  isTaskOverdue,
  nextHourEnd,
  nextHourStart,
  shiftTime,
  toMinutes,
} from '@/lib/time'
import { openTimePicker } from '@/lib/time-input'
import { goalUrl } from '@/lib/utils'
import { useOfflineStore } from '@/store/offline.store'

const EFFORT_CLASSES: Record<string, string> = {
  low: 'text-green-500 dark:text-green-400',
  medium: 'text-yellow-500 dark:text-yellow-400',
  high: 'text-red-500 dark:text-red-400',
}

const DATE_FNS_LOCALES: Record<string, typeof enUS> = { en: enUS, es, fr, ja, pt, zh: zhCN }
const RECURS_PERIODS = [
  'daily',
  'everyOtherDay',
  'weekly',
  'monthly',
  'weekend',
  'weekday',
  'days',
] as const
type RecursPeriod = (typeof RECURS_PERIODS)[number]
// Shown when "Specific days" is picked for a task that doesn't repeat yet.
const DEFAULT_DAYS = [1, 3, 5]

export type NodeDetail = {
  nodeId: Id<'nodes'>
  type: 'milestone' | 'target' | 'task'
  title: string
  description?: string | null
  category: string
  goalTitle: string
  milestoneTitle?: string
  targetTitle?: string
  effort?: 'low' | 'medium' | 'high'
  completedAt?: number | null
  status?: string
  dueDate?: number | null
  recurs?: boolean
  recursPeriod?: string
  recursDays?: number[]
  recurrenceDay?: number
  recurrenceTime?: string
  durationMinutes?: number
  allDay?: boolean
  // Milestone/target task counts, for callers without a goal tree subscription.
  progress?: { done: number; total: number }
}

export function NodeDetailSheet({
  node,
  goalId,
  onClose,
  onDelete,
  draft = false,
  viewerIsCoach = false,
}: {
  node: NodeDetail | null
  // Goal being viewed — lets status/complete updates optimistically patch the
  // goal tree so rows in the milestone/target sections reflect changes instantly.
  goalId?: Id<'nodes'>
  onClose: () => void
  // When provided, deletion is delegated to the caller (in-memory draft trees).
  // Otherwise the node is removed from the DB by id.
  onDelete?: () => void
  // Unsaved draft (preview/onboarding): hide status actions that need a saved node.
  draft?: boolean
  // The client's coach edits the plan but can't tick tasks off.
  viewerIsCoach?: boolean
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const remove = useMutation(api.nodes.remove).withOptimisticUpdate((localStore, { nodeId }) => {
    // Drop the node from the goal tree immediately so confirming delete is instant.
    // A node can be a milestone, target or task, so filter at every level.
    if (goalId) {
      const tree = localStore.getQuery(api.nodes.getGoalTree, { goalId })
      if (tree) {
        localStore.setQuery(
          api.nodes.getGoalTree,
          { goalId },
          {
            ...tree,
            milestones: tree.milestones
              .filter((ms) => ms._id !== nodeId)
              .map((ms) => ({
                ...ms,
                targets: ms.targets
                  .filter((tg) => tg._id !== nodeId)
                  .map((tg) => ({ ...tg, tasks: tg.tasks.filter((t) => t._id !== nodeId) })),
              })),
          },
        )
      }
    }
    // Also clear it from the habits list (recurring rows show there too).
    const habitList = localStore.getQuery(api.nodes.getHabits)
    if (habitList) {
      localStore.setQuery(
        api.nodes.getHabits,
        {},
        habitList.filter((h) => h._id !== nodeId),
      )
    }
  })

  function handleDelete() {
    if (!node) return
    if (onDelete) onDelete()
    else void remove({ nodeId: node.nodeId }).catch(() => {})
    onClose()
  }

  const color = node ? (CATEGORY_COLORS[node.category?.toLowerCase()] ?? '#6b6b6b') : '#6b6b6b'
  const { isPro } = useIsPro()
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const chat = useTaskExplain()
  const { ask, isLoading, streamedText, isStreaming, reset } = chat
  const askChips = useAskChips(node?.type ?? 'task')
  const { voice: coachVoice, name: coachName } = useCoachPersona(!!node)
  const responseRef = useRef<HTMLDivElement>(null)

  const [locallyDone, setLocallyDone] = useState(false)
  const [inProgress, setInProgress] = useState(false)
  const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const complete = useMutation(api.nodes.complete).withOptimisticUpdate((localStore, { taskId }) =>
    completeNode(localStore, taskId),
  )
  const uncomplete = useMutation(api.nodes.uncomplete).withOptimisticUpdate(
    (localStore, { taskId }) => uncompleteNode(localStore, taskId),
  )
  const setTaskStatus = useSetTaskStatus(goalId ? { goalId } : undefined)
  const online = useOnlineStatus()
  const enqueueOp = useOfflineStore((s) => s.enqueue)

  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [localTitle, setLocalTitle] = useState('')
  const [localDesc, setLocalDesc] = useState<string | null>(null)
  const [noteKey, setNoteKey] = useState(0)
  const renameRef = useRef<HTMLTextAreaElement>(null)
  const tapPositionRef = useRef<{ x: number; y: number } | null>(null)
  const renameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rename = useMutation(api.nodes.rename).withOptimisticUpdate(optimisticRename)
  const titleHint = useSavedHint()

  function saveTitle(raw: string) {
    if (!node) return
    const { title, description } =
      node.type === 'task' ? parseTaskTitle(raw) : { title: raw, description: null }
    if (!title) return
    setLocalTitle(title)
    if (description) {
      setLocalDesc(description)
      setNoteKey((k) => k + 1)
    }
    void titleHint.track(
      rename({ nodeId: node.nodeId, title, ...(description && { description }) }),
    )
  }

  const [localDueDate, setLocalDueDate] = useState<number | undefined>(undefined)
  const [dueDateOpen, setDueDateOpen] = useState(false)
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(undefined)
  const [timeStr, setTimeStr] = useState('09:00')
  const [localDuration, setLocalDuration] = useState(60)
  const [localMinutes, setLocalMinutes] = useState<number | undefined>()
  const [localRecursPeriod, setLocalRecursPeriod] = useState<RecursPeriod | null>(null)
  const [localRecurrenceDay, setLocalRecurrenceDay] = useState<number>(1)
  const [localDays, setLocalDays] = useState<number[]>([])
  const [daysOpen, setDaysOpen] = useState(false)
  const [recursOpen, setRecursOpen] = useState(false)
  const [recurrenceTimeStr, setRecurrenceTimeStr] = useState('09:00')
  const [recurrenceEndTimeStr, setRecurrenceEndTimeStr] = useState('10:00')
  // Optimistic all-day flag — reflects the toggle before the server round-trips.
  const [localAllDay, setLocalAllDay] = useState(false)
  // Stable matcher so react-day-picker doesn't churn cells (flicker + swallowed
  // clicks) when the surrounding screen re-renders with the popover open.
  const dueDisabled = useMemo(() => ({ before: new Date(new Date().setHours(0, 0, 0, 0)) }), [])
  const updateDueDate = useMutation(api.nodes.updateTaskDueDate).withOptimisticUpdate(
    optimisticDueDate,
  )
  const updateRecursPeriod = useMutation(api.nodes.updateRecursPeriod).withOptimisticUpdate(
    optimisticRecurs,
  )
  const updateTaskDuration = useMutation(api.nodes.updateTaskDuration).withOptimisticUpdate(
    optimisticDuration,
  )
  const setTaskAllDay = useMutation(api.nodes.setTaskAllDay).withOptimisticUpdate(optimisticAllDay)
  const locale = DATE_FNS_LOCALES[i18next.language.split('-')[0]] ?? enUS
  const goalTree = useQuery(api.nodes.getGoalTree, goalId && node ? { goalId } : 'skip')
  const progress = useMemo(() => {
    if (!node || node.type === 'task') return null
    if (node.progress) return node.progress
    if (!goalTree) return null
    const tasks =
      node.type === 'milestone'
        ? goalTree.milestones
            .find((ms) => ms._id === node.nodeId)
            ?.targets.flatMap((tg) => tg.tasks)
        : goalTree.milestones.flatMap((ms) => ms.targets).find((tg) => tg._id === node.nodeId)
            ?.tasks
    if (!tasks) return null
    return { done: tasks.filter((tk) => tk.completedAt !== null).length, total: tasks.length }
  }, [node, goalTree])

  const path = useMemo((): DetailPathNode[] => {
    if (!node) return []
    const go = (search?: { openMilestone: string; focusNode?: string }) =>
      goalId && goalTree
        ? () => {
            onClose()
            navigate({
              to: '/goals/$goalId',
              params: { goalId: goalUrl(goalTree.title, goalId).replace('/goals/', '') },
              search,
            })
          }
        : undefined
    let ms: { _id: string; title: string } | undefined
    let tg: { _id: string; title: string } | undefined
    for (const m of goalTree?.milestones ?? []) {
      const target = m.targets.find(
        (x) => x._id === node.nodeId || x.tasks.some((tk) => tk._id === node.nodeId),
      )
      if (m._id === node.nodeId || target) {
        ms = m
        if (target && target._id !== node.nodeId) tg = target
        break
      }
    }
    const milestoneTitle = ms?.title ?? node.milestoneTitle
    const targetTitle = node.type === 'task' ? (tg?.title ?? node.targetTitle) : undefined
    return [
      { title: goalTree?.title ?? node.goalTitle, onClick: go() },
      ...(node.type !== 'milestone' && milestoneTitle
        ? [{ title: milestoneTitle, onClick: ms && go({ openMilestone: ms._id }) }]
        : []),
      ...(targetTitle
        ? [
            {
              title: targetTitle,
              onClick: ms && tg && go({ openMilestone: ms._id, focusNode: tg._id }),
            },
          ]
        : []),
    ].filter((n) => n.title)
  }, [node, goalId, goalTree, navigate, onClose])

  useEffect(() => {
    reset()
    setLocallyDone(node?.completedAt != null)
    setInProgress(node?.status === 'in-progress')
    setRenaming(false)
    setLocalTitle(node?.title ?? '')
    setLocalDesc(node?.description ?? null)
    const hasRecurring = !!(node?.recursPeriod || node?.recurs)
    setLocalRecursPeriod(
      (node?.recursPeriod as RecursPeriod | undefined) ?? (node?.recurs ? 'daily' : null),
    )
    setLocalRecurrenceDay(node?.recurrenceDay ?? 1)
    setLocalDays(scheduleDays(node?.recursPeriod, node?.recurrenceDay, node?.recursDays))
    setDaysOpen(false)
    setLocalDuration(node?.durationMinutes ?? 60)
    setLocalMinutes(node?.durationMinutes)
    setLocalAllDay(!!node?.allDay)
    const dd = hasRecurring ? undefined : (node?.dueDate ?? undefined)
    setLocalDueDate(dd)
    if (dd) {
      const d = new Date(dd)
      setSelectedDay(d)
      setTimeStr(
        `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
      )
    } else {
      setSelectedDay(undefined)
      setTimeStr('09:00')
    }
    const startTime = node?.recurrenceTime || '09:00'
    setRecurrenceTimeStr(startTime)
    const [sh = 9, sm = 0] = startTime.split(':').map(Number)
    const endMins = sh * 60 + sm + (node?.durationMinutes ?? 60)
    setRecurrenceEndTimeStr(
      `${String(Math.floor(endMins / 60) % 24).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}`,
    )
    if (completeTimerRef.current) {
      clearTimeout(completeTimerRef.current)
      completeTimerRef.current = null
    }
    if (renameTimerRef.current) {
      clearTimeout(renameTimerRef.current)
      renameTimerRef.current = null
    }
  }, [node?.nodeId])

  const onResponseScroll = useStickToBottom(responseRef, streamedText, chat.pendingQuestion)

  useEffect(() => {
    const el = renameRef.current
    if (!el) return
    const size = () => {
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    }
    size()
    // Re-measure after layout settles (open + keyboard viewport resize).
    requestAnimationFrame(size)
    const timer = setTimeout(size, 120)
    el.focus()
    const pos = tapPositionRef.current
    if (pos) {
      tapPositionRef.current = null
      const offset = caretOffsetFromPoint(el, pos.x, pos.y)
      if (offset !== null) el.setSelectionRange(offset, offset)
      else el.setSelectionRange(el.value.length, el.value.length)
    } else {
      el.setSelectionRange(el.value.length, el.value.length)
    }
    return () => clearTimeout(timer)
  }, [renaming])

  const effortLabel: Record<string, string> = {
    low: t('today.effortLow'),
    medium: t('today.effortMedium'),
    high: t('today.effortHigh'),
  }

  function applyDueDate(d: Date) {
    setLocalDueDate(d.getTime())
    setSelectedDay(d)
    updateDueDate({ taskId: node!.nodeId, dueDate: d.getTime() }).catch(() => {})
    if (localRecursPeriod) clearRecurring()
    // Close on day-select (matches the Today sidesheet) — picking a day flips the
    // set/add popover, so keeping it open remounts the calendar and flickers.
    setDueDateOpen(false)
  }

  function handleDaySelect(day: Date | undefined) {
    if (!day) return
    const [h, m] = timeStr.split(':').map(Number)
    day.setHours(h, m, 0, 0)
    applyDueDate(day)
  }

  function handleTimeChange(val: string) {
    setTimeStr(val)
    if (!selectedDay) return
    const [h, m] = val.split(':').map(Number)
    const d = new Date(selectedDay)
    d.setHours(h, m, 0, 0)
    applyDueDate(d)
  }

  function handleDatePreset(preset: 'today' | 'tomorrow' | 'thisWeekend' | 'nextWeek') {
    const d = new Date()
    switch (preset) {
      case 'tomorrow':
        d.setDate(d.getDate() + 1)
        break
      case 'thisWeekend': {
        const day = d.getDay()
        d.setDate(d.getDate() + (day === 6 ? 0 : 6 - day))
        break
      }
      case 'nextWeek': {
        const day = d.getDay()
        d.setDate(d.getDate() + (day === 0 ? 1 : 8 - day))
        break
      }
    }
    const [h = 9, m = 0] = timeStr.split(':').map(Number)
    d.setHours(h, m, 0, 0)
    applyDueDate(d)
  }

  function clearRecurring() {
    setLocalRecursPeriod(null)
    updateRecursPeriod({
      taskId: node!.nodeId,
      recursPeriod: undefined,
      recurrenceTzOffset: new Date().getTimezoneOffset(),
    }).catch(() => {})
  }

  function toggleAllDay() {
    if (!node) return
    const next = !localAllDay
    setLocalAllDay(next)
    if (next) {
      setTaskAllDay({ taskId: node.nodeId, allDay: true }).catch(() => setLocalAllDay(false))
      return
    }
    // Switching to timed → set the next-hour range BEFORE clearing all-day, so the
    // list row never flashes as "timed at 09:00" (which reads as overdue).
    const start = nextHourStart()
    const end = nextHourEnd()
    if (localRecursPeriod) {
      setRecurrenceTimeStr(start)
      setRecurrenceEndTimeStr(end)
      updateRecursPeriod({
        taskId: node.nodeId,
        recursPeriod: localRecursPeriod,
        recurrenceTime: start,
        recurrenceTzOffset: new Date().getTimezoneOffset(),
      }).catch(() => {})
      updateTaskDuration({ taskId: node.nodeId, durationMinutes: 60 }).catch(() => {})
    } else {
      setTimeStr(start)
      setLocalDuration(60)
      handleTimeChange(start)
      updateTaskDuration({ taskId: node.nodeId, durationMinutes: 60 }).catch(() => {})
    }
    setTaskAllDay({ taskId: node.nodeId, allDay: false }).catch(() => setLocalAllDay(true))
  }

  // One-off (dated) event end edit — start reuses handleTimeChange (rewrites the
  // dueDate time); end rewrites durationMinutes.
  function handleDueEndChange(v: string) {
    if (!node) return
    const newEnd = coerceEnd(timeStr, v)
    const [sh = 9, sm = 0] = timeStr.split(':').map(Number)
    const [eh = 10, em = 0] = newEnd.split(':').map(Number)
    const dur = Math.max(15, eh * 60 + em - (sh * 60 + sm))
    setLocalDuration(dur)
    updateTaskDuration({ taskId: node.nodeId, durationMinutes: dur }).catch(() => {})
  }

  function setRecurTime(start: string, durationMinutes: number) {
    if (!node) return
    setRecurrenceTimeStr(start)
    setRecurrenceEndTimeStr(shiftTime(start, durationMinutes))
    updateRecursPeriod({
      taskId: node.nodeId,
      recursPeriod: localRecursPeriod ?? undefined,
      recurrenceTime: start,
      recurrenceTzOffset: new Date().getTimezoneOffset(),
    }).catch(() => {})
    updateTaskDuration({ taskId: node.nodeId, durationMinutes }).catch(() => {})
    // Cleared after the time is written so the row never reads as "timed at 09:00".
    if (localAllDay) {
      setLocalAllDay(false)
      setTaskAllDay({ taskId: node.nodeId, allDay: false }).catch(() => setLocalAllDay(true))
    }
  }

  function setRecurAllDay() {
    if (!node || localAllDay) return
    setLocalAllDay(true)
    setTaskAllDay({ taskId: node.nodeId, allDay: true }).catch(() => setLocalAllDay(false))
  }

  function applyDays(days: number[]) {
    if (!node) return
    const s = scheduleFromDays(days)
    setLocalDays(days)
    setLocalRecursPeriod(s.recursPeriod)
    if (s.recurrenceDay !== undefined) setLocalRecurrenceDay(s.recurrenceDay)
    updateRecursPeriod({
      taskId: node.nodeId,
      recursPeriod: 'days',
      recursDays: days,
      recurrenceTzOffset: new Date().getTimezoneOffset(),
      ...(localRecursPeriod && { recurrenceTime: recurrenceTimeStr }),
    }).catch(() => {})
    if (localDueDate) {
      setLocalDueDate(undefined)
      updateDueDate({ taskId: node.nodeId, dueDate: undefined }).catch(() => {})
    }
  }

  function selectPeriod(p: RecursPeriod) {
    if (!node) return
    setDaysOpen(p === 'days')
    if (p === 'days') {
      if (localRecursPeriod)
        setLocalDays(scheduleDays(localRecursPeriod, localRecurrenceDay, localDays))
      else applyDays(DEFAULT_DAYS)
      return
    }
    const wasSet = !!localRecursPeriod
    setLocalRecursPeriod(p)
    updateRecursPeriod({
      taskId: node.nodeId,
      recursPeriod: p,
      recurrenceTzOffset: new Date().getTimezoneOffset(),
      ...(wasSet && { recurrenceTime: recurrenceTimeStr }),
      ...((p === 'weekly' || p === 'monthly') && { recurrenceDay: localRecurrenceDay }),
    }).catch(() => {})
    if (localDueDate) {
      setLocalDueDate(undefined)
      updateDueDate({ taskId: node.nodeId, dueDate: undefined }).catch(() => {})
    }
  }

  function selectRecurrenceDay(d: number) {
    if (!node || (localRecursPeriod !== 'weekly' && localRecursPeriod !== 'monthly')) return
    setLocalRecurrenceDay(d)
    updateRecursPeriod({
      taskId: node.nodeId,
      recursPeriod: localRecursPeriod,
      recurrenceDay: d,
      recurrenceTime: recurrenceTimeStr,
      recurrenceTzOffset: new Date().getTimezoneOffset(),
    }).catch(() => {})
  }

  function toggleComplete() {
    if (!node) return
    if (!locallyDone) {
      setLocallyDone(true)
      completeTimerRef.current = setTimeout(() => {
        playDoneSound()
        if (online) {
          complete({ taskId: node.nodeId }).catch(() => setLocallyDone(false))
        } else {
          enqueueOp({ kind: 'complete', args: { taskId: node.nodeId as string } })
        }
        completeTimerRef.current = null
      }, 600)
      return
    }
    setLocallyDone(false)
    if (completeTimerRef.current) {
      clearTimeout(completeTimerRef.current)
      completeTimerRef.current = null
    } else if (online) {
      uncomplete({ taskId: node.nodeId }).catch(() => setLocallyDone(true))
    } else {
      enqueueOp({ kind: 'uncomplete', args: { taskId: node.nodeId as string } })
    }
  }

  function toggleInProgress() {
    if (!node) return
    const next = inProgress ? 'backlog' : 'in-progress'
    setInProgress(!inProgress)
    setTaskStatus({ taskId: node.nodeId, status: next }).catch(() => setInProgress(inProgress))
  }

  function clearDueDate() {
    if (!node) return
    setLocalDueDate(undefined)
    setSelectedDay(undefined)
    updateDueDate({ taskId: node.nodeId, dueDate: undefined }).catch(() => {})
  }

  function dueLabel(ms: number) {
    const d = new Date(ms)
    const dateStr = d.toLocaleDateString(i18next.language, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
    if (localAllDay) return dateStr
    const start = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    // Show from–to once the range spans more than an hour.
    if (localDuration > 60) return `${dateStr}, ${start} – ${shiftTime(start, localDuration)}`
    return `${dateStr}, ${start}`
  }

  function handleAsk(text: string) {
    if (!node || !text.trim() || isLoading || isStreaming) return
    if (!isPro) {
      setUpgradeOpen(true)
      return
    }
    ask(node.title, node.goalTitle, text, node.nodeId)
  }

  const valueBtn =
    'flex min-w-0 cursor-pointer items-center gap-1.5 rounded-sm outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50'

  const menuItems: DetailMenuItem[] = [
    {
      key: 'delete',
      label: t('inbox.deleteEllipsis'),
      icon: <Trash2 className="h-4 w-4" />,
      destructive: true,
      inline: true,
      onSelect: handleDelete,
    },
  ]

  const dueDateBody = (
    <>
      <div className="flex w-full flex-wrap justify-center gap-1 px-3 pt-2 pb-0">
        {(['today', 'tomorrow', 'thisWeekend', 'nextWeek'] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => handleDatePreset(p)}
            className="cursor-pointer rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            {t(`inbox.datePreset.${p}`)}
          </button>
        ))}
      </div>
      <Calendar
        mode="single"
        disabled={dueDisabled}
        selected={selectedDay}
        defaultMonth={selectedDay ?? new Date()}
        onSelect={handleDaySelect}
        locale={locale}
        classNames={{
          root: 'w-full',
          range_start: '',
          range_middle: '',
          range_end: '',
          today: 'rounded-(--cell-radius) bg-muted text-foreground',
        }}
      />
      <div className="flex w-full items-center justify-center gap-2 border-t border-border px-3 pt-3 pb-1">
        <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div className={cn('flex items-center gap-1.5', localAllDay && 'opacity-40')}>
          <input
            type="time"
            onClick={openTimePicker}
            lang="en-GB"
            value={timeStr}
            onChange={(e) => handleTimeChange(e.target.value)}
            className="cursor-pointer rounded bg-transparent px-1 text-sm text-foreground outline-none transition-colors hover:bg-muted"
          />
          <span className="text-muted-foreground/40 text-xs select-none">→</span>
          <input
            type="time"
            onClick={openTimePicker}
            lang="en-GB"
            value={shiftTime(timeStr, localDuration)}
            onChange={(e) => handleDueEndChange(e.target.value)}
            className="cursor-pointer rounded bg-transparent px-1 text-sm text-foreground outline-none transition-colors hover:bg-muted"
          />
        </div>
      </div>
      <div className="w-full px-3 pb-2">
        <button
          type="button"
          onClick={toggleAllDay}
          className={cn(
            'flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors',
            localAllDay
              ? 'border-primary/40 bg-primary/10 text-primary-text hover:bg-primary/15'
              : 'border-border/60 text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          <Sun className="h-3 w-3 shrink-0" />
          {t('inbox.allDay')}
        </button>
      </div>
    </>
  )

  const periodOption = (p: RecursPeriod) => (
    <span
      className={cn(
        'flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted',
        (p === 'days' ? daysOpen || localRecursPeriod === 'days' : localRecursPeriod === p)
          ? 'text-primary-text'
          : 'text-foreground',
      )}
    >
      <RefreshCw className="h-3 w-3" strokeWidth={2.5} />
      {t(`inbox.period.${p}`)}
    </span>
  )

  const dayOption = (d: number, label: string | number) => (
    <PopoverClose
      key={d}
      className="flex h-7 w-7 cursor-pointer items-center justify-center rounded"
      onClick={() => selectRecurrenceDay(d)}
    >
      <span
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded text-xs font-medium transition-colors hover:bg-muted',
          localRecurrenceDay === d ? 'bg-primary text-primary-foreground' : 'text-foreground',
        )}
      >
        {label}
      </span>
    </PopoverClose>
  )

  return (
    <>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="ask-ai" />
      <Sheet open={!!node} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="flex w-80 flex-col p-0 sm:max-w-sm">
          {node && (
            <div className="flex flex-1 flex-col overflow-hidden">
              {/* Top bar — pr clears the sheet's own close button */}
              <div className="flex shrink-0 items-center gap-2 pt-1.5 pr-11 pl-4">
                <DetailKindLabel>
                  {t(`goals.nodeType${node.type.charAt(0).toUpperCase() + node.type.slice(1)}`)}
                </DetailKindLabel>
                <DetailMenu
                  items={menuItems}
                  confirmTitle={
                    node.type === 'task'
                      ? t('common.deleteTaskTitle')
                      : t('common.deleteNodeTitle', { label: localTitle || node.title })
                  }
                  confirmDescription={
                    node.type === 'task'
                      ? t('common.deleteTaskDesc', { title: localTitle || node.title })
                      : t('common.deleteNodeDesc')
                  }
                />
              </div>

              <div
                ref={responseRef}
                onScroll={onResponseScroll}
                className="flex flex-col gap-5 flex-1 min-w-0 overflow-y-auto overflow-x-hidden px-4 pt-3 pb-4"
              >
                {/* Title */}
                {renaming ? (
                  <textarea
                    ref={renameRef}
                    rows={1}
                    value={renameValue}
                    onChange={(e) => {
                      const val = e.target.value
                      setRenameValue(val)
                      e.target.style.height = 'auto'
                      e.target.style.height = `${e.target.scrollHeight}px`
                      if (renameTimerRef.current) clearTimeout(renameTimerRef.current)
                      renameTimerRef.current = setTimeout(() => {
                        const trimmed = val.trim()
                        if (!trimmed) return
                        saveTitle(trimmed)
                      }, 500)
                    }}
                    onBlur={() => {
                      if (renameTimerRef.current) {
                        clearTimeout(renameTimerRef.current)
                        renameTimerRef.current = null
                      }
                      const trimmed = renameValue.trim()
                      if (trimmed) saveTitle(trimmed)
                      setRenaming(false)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        e.currentTarget.blur()
                      }
                      if (e.key === 'Escape') {
                        if (renameTimerRef.current) {
                          clearTimeout(renameTimerRef.current)
                          renameTimerRef.current = null
                        }
                        setRenaming(false)
                      }
                    }}
                    className="min-h-9 w-full resize-none overflow-y-auto bg-transparent text-2xl font-bold leading-tight text-foreground outline-none"
                  />
                ) : (
                  <p
                    className="text-2xl font-bold leading-tight text-foreground cursor-text hover:underline decoration-dashed underline-offset-4"
                    onClick={(e) => {
                      tapPositionRef.current = { x: e.clientX, y: e.clientY }
                      setRenameValue(localTitle || node.title)
                      setRenaming(true)
                    }}
                  >
                    {localTitle || node.title}
                  </p>
                )}
                <SavedHint status={titleHint.status} />

                {node.type === 'task' && !draft && (
                  <TaskNoteField
                    key={`${node.nodeId}-${noteKey}`}
                    value={localDesc}
                    onChange={setLocalDesc}
                    onSave={(description) =>
                      rename({
                        nodeId: node.nodeId,
                        title: localTitle || node.title,
                        description,
                      })
                    }
                  />
                )}

                {node.type === 'task' && !draft && !viewerIsCoach && (
                  <DetailPrimaryAction kind="task" done={locallyDone} onClick={toggleComplete} />
                )}
                {progress && progress.total > 0 && (
                  <MilestoneProgress done={progress.done} total={progress.total} />
                )}

                {path.length > 0 && <DetailPartOf color={color} path={path} />}

                <DetailRows>
                  {node.type === 'task' && !draft && (
                    <DetailRow label={t('inbox.rowDue')}>
                      <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
                        <PopoverTrigger
                          className={cn(
                            valueBtn,
                            !localDueDate
                              ? 'text-muted-foreground'
                              : isDueTodayOrOverdue(localDueDate) &&
                                  'text-red-500 dark:text-red-400',
                          )}
                        >
                          {localDueDate ? (
                            `${isTaskOverdue(localDueDate, localAllDay) ? `${t('inbox.overdue')} · ` : ''}${dueLabel(localDueDate)}`
                          ) : (
                            <>
                              {t('inbox.addDueDate')}
                              <Plus className="h-3 w-3" />
                            </>
                          )}
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-[min(20rem,92vw)] p-0"
                          side="bottom"
                          align="start"
                        >
                          {dueDateBody}
                        </PopoverContent>
                      </Popover>
                      {localDueDate && (
                        <button
                          type="button"
                          aria-label={t('common.clear', { defaultValue: 'Clear' })}
                          onClick={clearDueDate}
                          className="cursor-pointer rounded-sm text-muted-foreground/60 outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50"
                        >
                          <X className="h-3 w-3" strokeWidth={2.5} />
                        </button>
                      )}
                    </DetailRow>
                  )}

                  {node.type === 'task' && node.effort && (
                    <DetailRow label={t('filter.effort')}>
                      <span className={EFFORT_CLASSES[node.effort] ?? EFFORT_CLASSES.medium}>
                        {effortLabel[node.effort]}
                      </span>
                    </DetailRow>
                  )}

                  {node.type === 'task' &&
                    !draft &&
                    !locallyDone &&
                    (localAllDay || (!localDueDate && !node.recurrenceTime)) && (
                      <DetailRow label={t('timeBudget.taskTime')}>
                        <TaskDurationPicker
                          value={localMinutes}
                          className={valueBtn}
                          onChange={(m) => {
                            const prev = localMinutes
                            setLocalMinutes(m)
                            setLocalDuration(m)
                            updateTaskDuration({ taskId: node.nodeId, durationMinutes: m }).catch(
                              () => setLocalMinutes(prev),
                            )
                          }}
                        />
                      </DetailRow>
                    )}

                  {node.type === 'task' && !draft && !locallyDone && (
                    <DetailRow label={t('filter.status')} onClick={toggleInProgress}>
                      <TaskStatusIcon status={inProgress ? 'in_progress' : 'pending'} size={14} />
                      {inProgress ? t('goals.inProgress') : t('inbox.notStarted')}
                    </DetailRow>
                  )}

                  {node.type === 'task' && !draft && (
                    <DetailRow label={t('inbox.rowRepeats')}>
                      <Popover
                        open={recursOpen}
                        onOpenChange={(o) => {
                          setRecursOpen(o)
                          if (!o) setDaysOpen(false)
                        }}
                      >
                        <PopoverTrigger
                          className={cn(valueBtn, !localRecursPeriod && 'text-muted-foreground')}
                        >
                          {localRecursPeriod ? (
                            <>
                              {periodLabel(t, i18next.language, localRecursPeriod, localDays)}
                              {localRecursPeriod === 'weekly' &&
                                ` · ${new Intl.DateTimeFormat(i18next.language, { weekday: 'short' }).format(new Date(2025, 0, 5 + localRecurrenceDay))}`}
                              {localRecursPeriod === 'monthly' && ` · ${localRecurrenceDay}`}
                            </>
                          ) : (
                            t('inbox.repeatsNever', { defaultValue: 'Never' })
                          )}
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto min-w-[140px] p-1"
                          side="bottom"
                          align="start"
                        >
                          {RECURS_PERIODS.map((p) =>
                            p === 'weekly' || p === 'monthly' || p === 'days' ? (
                              // Stays open so the day picker below can be used.
                              <button
                                key={p}
                                type="button"
                                className="w-full"
                                onClick={() => selectPeriod(p)}
                              >
                                {periodOption(p)}
                              </button>
                            ) : (
                              <PopoverClose
                                key={p}
                                className="w-full"
                                onClick={() => selectPeriod(p)}
                              >
                                {periodOption(p)}
                              </PopoverClose>
                            ),
                          )}
                          {(daysOpen || localRecursPeriod === 'days') && (
                            <RecursDaysChips
                              className="mt-1 border-t border-border/40 px-2 py-2"
                              days={localDays}
                              onChange={applyDays}
                            />
                          )}
                          {localRecursPeriod === 'weekly' && !daysOpen && (
                            <div className="mt-1 flex gap-0.5 border-t border-border/40 px-2 py-2">
                              {[0, 1, 2, 3, 4, 5, 6].map((d) =>
                                dayOption(
                                  d,
                                  new Intl.DateTimeFormat(i18next.language, {
                                    weekday: 'narrow',
                                  }).format(new Date(2025, 0, 5 + d)),
                                ),
                              )}
                            </div>
                          )}
                          {localRecursPeriod === 'monthly' && (
                            <div className="mt-1 grid w-[200px] grid-cols-7 gap-0.5 border-t border-border/40 px-2 py-2">
                              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) =>
                                dayOption(d, d),
                              )}
                            </div>
                          )}
                          {localRecursPeriod && (
                            <PopoverClose className="w-full" onClick={clearRecurring}>
                              <span className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10">
                                <X className="h-3 w-3" strokeWidth={2.5} />
                                {t('inbox.removeRecurring')}
                              </span>
                            </PopoverClose>
                          )}
                        </PopoverContent>
                      </Popover>
                      {localRecursPeriod && (
                        <HabitTimePicker
                          allDay={localAllDay}
                          start={recurrenceTimeStr}
                          durationMinutes={Math.max(
                            15,
                            toMinutes(recurrenceEndTimeStr) - toMinutes(recurrenceTimeStr),
                          )}
                          onSet={setRecurTime}
                          onAllDay={setRecurAllDay}
                        />
                      )}
                    </DetailRow>
                  )}
                </DetailRows>

                <DetailActions
                  items={menuItems}
                  confirmTitle={
                    node.type === 'task'
                      ? t('common.deleteTaskTitle')
                      : t('common.deleteNodeTitle', { label: localTitle || node.title })
                  }
                  confirmDescription={
                    node.type === 'task'
                      ? t('common.deleteTaskDesc', { title: localTitle || node.title })
                      : t('common.deleteNodeDesc')
                  }
                />

                <AskCoachThread chat={chat} voice={coachVoice} chips={askChips} />
              </div>

              <AskCoachComposer
                subject={node?.type ?? 'task'}
                chips={askChips}
                chat={chat}
                coachName={coachName}
                onSend={handleAsk}
                resetKey={node?.nodeId}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  )
}
